<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Now, it’s time to make a simple game. The skills you learn in this process will help you to
      build the skills you’ll need to build your own games in the future. But, for now, we’ll start
      with a straightforward and simple game on which to hone our skills.
    </p>
    <p class="mb-5">Let’s get started by learning a little about coding!</p>
    <h4 class="mb-5 italic">Writing Code</h4>
    <img
      src="@/assets/images/experiences/gamesdeveloper/activity_6_image.jpg"
      alt="Two developers reviewing code together on dual monitors."
      class="my-2 ml-2 mb-2 float-right"
    />

    <p class="mb-5">
      These days, large teams of people highly-trained in particular areas often work together to
      produce complex games.
    </p>
    <p class="mb-5">
      While it’s true that these days, due to specialisation, not all Games Developers know how to
      write code, it’s also true that it is a great asset to have if you are going to make this your
      career.
    </p>
    <p class="mb-5">
      Many programming languages can be used to write the code for video games, but some of the most
      frequently used include:
    </p>
    <ul class="default-list">
      <li>C++</li>
      <li>Java</li>
      <li>HTML5</li>
      <li>CSS3</li>
      <li>JavaScript</li>
      <li>SQL</li>
    </ul>
    <p class="mb-5">
      Becoming proficient in writing code takes time and practice, but even having a basic grasp of
      writing code can be a great help in the game development process.
    </p>
    <p class="mb-5">
      Of the programming languages listed above, C++ is the most widely used for video games.
    </p>
    <p class="mb-5">
      If you’d like to learn more about how to use C++, check out the free text-based tutorials on
      this website:
    </p>

    <p class="mb-5 text-center">
      <button-link-wrapper text="Learn C++" link="https://www.learncpp.com/" />
    </p>
    <p class="mb-5">
      Or, if you prefer to work your way through some video tutorials, follow this link for a free
      and very detailed course on C++ from Udemy:
    </p>
    <p class="mb-5 text-center">
      <button-link-wrapper
        text="Free C++ Tutorial"
        link="https://www.udemy.com/course/free-learn-c-tutorial-beginners/?LSNPUBID=JVFxdTr9V80&ranEAID=JVFxdTr9V80&ranMID=39197&ranSiteID=JVFxdTr9V80-Pp77KzMEBFcXft4Bzl85Ag&utm_medium=udemyads&utm_source=aff-campaign"
      />
    </p>
    <p class="mb-5">
      In the next activity Creating a Prototype, you will learn more about game development by
      making your own version of a basic arcade game.
    </p>
    <p class="mb-5">
      You’ll have two options to choose from here. You will be able to build your game using a
      straightforward Drag and Drop builder, or you’ll get a chance to do some programming by
      following a video tutorial.
    </p>
    <h4 class="mb-5 italic">Creating a Prototype</h4>
    <p class="mb-5">
      In the prototype stage, the Games Developer creates a ‘draft’ version of their game.
    </p>
    <p class="mb-5">
      In this task, you’re going to use software called GameMaker Studio 2 to make your first simple
      game.
    </p>
    <p class="mb-5">
      You don’t have to use the characters and storyboard you created in the previous tasks here.
      This task aims to use the free software to create a simple game following the very helpful
      tutorial videos below. The tutorials are very detailed, but they are aimed at beginners, and
      they’ll walk you through step-by-step on how to make a classic arcade game.
    </p>
    <p class="mb-5">
      The instructor is excellent! But, sometimes, things might go a little too quickly for you. In
      that case, just re-watch the appropriate section of the video again.
    </p>
    <p class="mb-5">
      Your first step will be to download the GameMaker Studio 2 software. Over 2,000 institutions
      use this programme to teach game design, and it’s available on a 30-Day Free Trial via the
      link below:
    </p>
    <p class="mb-5 text-center">
      <button-link-wrapper
        text="https://www.yoyogames.com/get"
        link="https://www.yoyogames.com/get"
      />
    </p>
    <p class="mb-5">
      Once you have downloaded the programme, follow the onscreen instructions to install it onto
      your computer.
    </p>
    <p class="mb-5">
      Once you have the software installed onto your computer, it’s time to learn how to use it!
    </p>
    <h4 class="mb-5">Your Task: My First Game: Intro to GameMaker - Space Rocks</h4>
    <p class="mb-5">
      In this task you are going to create a simple arcade game called “Space Rocks”. You will need
      to work through the steps below.
    </p>
    <h4 class="mb-5">Part 1: My First Game - Intro to GameMaker - Space Rocks</h4>
    <p class="mb-5">
      Watch the tutorial video and follow the instructions to begin making your own version of this
      classic arcade game.
    </p>

    <responsive-iframe-wrapper :video="videos[1].video" :image="videos[1].image" />

    <p class="mb-5">
      Early on, you’ll have to choose to build your game using either the Drag and Drop (DnD)
      Builder or the programming language called GameMaker Language (GML).
    </p>
    <p class="mb-5">
      If you don’t know much about coding yet, it’s probably best to use the DnD builder.
    </p>
    <p class="mb-5">
      However, you can use the GML option if you want to use some code in your design.
    </p>
    <p class="mb-5">
      Whichever option you choose, stay with the same builder throughout the course by choosing the
      appropriate version of each part below. If you use Drag and Drop (DnD), use the left hand tab.
      If you choose to use GameMaker Language (GML) then follow the process through using the right
      hand tab:
    </p>

    <vs-tabs alignment="fixed" class="overwrite-tabs">
      <vs-tab label="Drag and Drop (DnD)">
        <div class="tab-text my-5">
          <p class="mb-5">
            If you chose Drag and Drop, follow the tutorials for Parts 2-5 below and complete these
            steps in the Gamemaker software:
          </p>
          <h5 class="mb-5">Part 2: Set Up & Movement</h5>
          <responsive-iframe-wrapper :video="videos[2].video" :image="videos[2].image" />
          <h5 class="mb-5">Part 3: Attacking & Collisions</h5>
          <responsive-iframe-wrapper :video="videos[3].video" :image="videos[3].image" />
          <h5 class="mb-5">Part 4: Scores, Lives, and Effects</h5>
          <responsive-iframe-wrapper :video="videos[4].video" :image="videos[4].image" />
          <h5 class="mb-5">Part 5: Sound Effects & Polish</h5>
          <responsive-iframe-wrapper :video="videos[5].video" :image="videos[5].image" />
        </div>
      </vs-tab>
      <vs-tab label="GameMaker Language (GML)">
        <div class="tab-text my-5">
          <p class="mb-5">
            If you chose Gamemaker Language, follow the tutorials for Parts 2-5 below and complete
            these steps in the Gamemaker software:
          </p>
          <h5 class="mb-5">Part 2: Set Up & Movement</h5>
          <responsive-iframe-wrapper :video="videos[6].video" :image="videos[6].image" />
          <h5 class="mb-5">Part 3: Attacking & Collisions</h5>
          <responsive-iframe-wrapper :video="videos[7].video" :image="videos[7].image" />
          <h5 class="mb-5">Part 4: Scores, Lives, and Effects</h5>
          <responsive-iframe-wrapper :video="videos[8].video" :image="videos[8].image" />
          <h5 class="mb-5">Part 5: Sound Effects & Polish</h5>
          <responsive-iframe-wrapper :video="videos[9].video" :image="videos[9].image" />
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Finally………</h4>
    <p class="mb-5">
      Once your “Space Rocks” game prototype is complete, you should run it in the GameMaker
      software and take a video of it running with your phone.
    </p>
    <p class="mb-5">
      Just capture about 15-30 seconds of video of your game and then upload the video below.
    </p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      index="1"
      filetype="video"
      upload-btn-text="Upload Game Video"
    />

    <save-buttons :is-editable="isEditable" @save="save" />

    <div class="highlight-text mt-5">
      <p class="mb-5 text-bold">If you are Interested………. Full Playlist of Space Rocks Tutorials</p>

      <p class="mb-5">
        If you’ve followed the previous tutorial videos to completion, you should now have completed
        a basic version of Space Rocks. Of course, there’s a lot more you could add to this basic
        version of the game to increase its functionality and add more features. To learn how to do
        this, check out the full playlist of Space Rocks tutorials using the links below:
      </p>

      <p class="mb-2">
        <span style="width: 100px; display: inline-block">DND</span
        ><a
          href="https://www.youtube.com/playlist?list=PLhIbBGhnxj5LXZfeHYocAnngbPD9PCFlj"
          target="_blank"
          >https://www.youtube.com/playlist?list=PLhIbBGhnxj5LXZfeHYocAnngbPD9PCFlj</a
        >
      </p>
      <p>
        <span style="width: 100px; display: inline-block">GML</span
        ><a
          href="https://www.youtube.com/playlist?list=PLhIbBGhnxj5I8HuwoNhjgjDC8OQIf8D3Z"
          target="_blank"
          >https://www.youtube.com/playlist?list=PLhIbBGhnxj5I8HuwoNhjgjDC8OQIf8D3Z</a
        >
      </p>
    </div>
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/gamesdeveloper/intro_to_gamemaker_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/gamesdeveloper/intro_to_gamemaker.mp4',
        },
        2: {
          image: require('@/assets/images/experiences/gamesdeveloper/dnd_2_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/gamesdeveloper/dnd_2.mp4',
        },
        3: {
          image: require('@/assets/images/experiences/gamesdeveloper/dnd_3_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/gamesdeveloper/dnd_3.mp4',
        },
        4: {
          image: require('@/assets/images/experiences/gamesdeveloper/dnd_4_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/gamesdeveloper/dnd_4.mp4',
        },
        5: {
          image: require('@/assets/images/experiences/gamesdeveloper/dnd_5_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/gamesdeveloper/dnd_5.mp4',
        },
        6: {
          image: require('@/assets/images/experiences/gamesdeveloper/gml_2_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/gamesdeveloper/gml_2.mp4',
        },
        7: {
          image: require('@/assets/images/experiences/gamesdeveloper/gml_3_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/gamesdeveloper/gml_3.mp4',
        },
        8: {
          image: require('@/assets/images/experiences/gamesdeveloper/gml_4_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/gamesdeveloper/gml_4.mp4',
        },
        9: {
          image: require('@/assets/images/experiences/gamesdeveloper/gml_5_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/gamesdeveloper/gml_5.mp4',
        },
      },
      image1: require('@/assets/images/experiences/gamesdeveloper/bbc_article_button.png'),
      uploaded_files: {
        uploaded_file_1: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
